import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { ReactNode } from 'react';
import css from './VerbRail.module.scss';

export interface VerbRailItem {
  id: string;
  /** Tooltip text and accessible name — the rail itself shows no words. */
  label: string;
  icon: ReactNode;
  /**
   * Purple glyph. DESIGN.md's One Accent Rule caps purple at ≤10% of a screen and
   * reserves it for "start here", so at most ONE item per rail may set this.
   *
   * Which item that is follows the scope: normally the agent verb, but on an empty
   * library the accent moves to Add media, since that's the only thing worth doing
   * there (`buildLibraryRailItems`). Two accented items in one rail is no accent.
   */
  accent?: boolean;
  disabled?: boolean;
  /**
   * Why the item is disabled. While `disabled`, this replaces `label` as BOTH the tooltip and
   * the accessible name.
   *
   * It has to be both: the tooltip hangs off a wrapper span (a disabled button fires no hover
   * or focus events), so a reason left only there is mouse-only and a screen reader would never
   * reach it. Putting it in the name is safe because every reason LEADS with the verb — see
   * `verbReason` in `@lib/verbs`, which is a function precisely so that can't be forgotten.
   *
   * Don't say "unavailable" or "disabled" in it: the `disabled` attribute and the dimming
   * already carry the state. This is only the why.
   */
  disabledReason?: string;
  /**
   * Occupy the slot without rendering anything visible or interactive. For rails laid
   * out in a column (the desktop table), where an action that doesn't apply to one row
   * must not shift every other row's glyphs sideways.
   *
   * Distinct from `disabled`, which is a *visible* control the user can't use yet.
   */
  hidden?: boolean;
  /**
   * The click event is forwarded so an item can anchor a popover to its own button
   * (`event.currentTarget`) — the Libraries page's collapsed header verbs open the library
   * picker that way. Zero-argument handlers stay valid; most items ignore it.
   */
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

interface VerbRailProps {
  items: VerbRailItem[];
  className?: string;
  /**
   * `overlay` — on a thumbnail or video. Each glyph gets its own frosted-black bevel
   * circle, because it has to hold up against whatever frame is behind it.
   *
   * `inline` — on an opaque table cell or list row. Bare glyphs, circle on hover
   * only. The same treatment as `overlay` was tried here and read badly: three heavy
   * discs outweighing the pin and kebab beside them, which makes secondary actions
   * the loudest thing in the row.
   *
   * Both keep the 40px hit target; only the visual weight differs.
   */
  variant?: 'overlay' | 'inline';
}

/**
 * A row of bare icon buttons — the abbreviated form of the labelled verbs.
 *
 * "Teach once, abbreviate everywhere": one labelled action row inside a library
 * carries the words and the outcome lines, and every repeat of those verbs is
 * this rail. The words still live one layer away — in every button's tooltip and
 * `aria-label`, and on mobile in the ⋯ drawer. (Desktop kebabs no longer list the
 * verbs at all: wherever one of these rails is on screen, the menu beside it dropped
 * them as redundant, so the tooltip is the learnable surface there.)
 *
 * Every button cancels the click it sits inside. Library cards and mobile rows
 * are `<Link>`s and media cards navigate on click, so without `preventDefault`
 * a rail tap would also open the thing it acts on. This mirrors the contract
 * `LibraryMenuButton` already uses on these same cards.
 */
export const VerbRail = ({ items, className, variant = 'overlay' }: VerbRailProps) => (
  <div className={classNames(css.rail, css[variant], className)}>
    {items.map((item) =>
      item.hidden ? (
        // Spacer only: `visibility: hidden` keeps the slot's width so sibling rails
        // stay aligned. No tooltip (nothing to point at), out of the tab order, and
        // hidden from assistive tech — it isn't a control, it's layout.
        <span key={item.id} className={css.slot} aria-hidden="true">
          <button
            type="button"
            className={classNames(css.button, css.placeholder)}
            tabIndex={-1}
            disabled
          />
        </span>
      ) : (
        // A disabled MUI Tooltip child swallows the tooltip (no events fire on a
        // disabled button), so the wrapper span keeps the explanation reachable.
        <Tooltip
          key={item.id}
          // Reason when there is one and the item is off; the verb otherwise.
          title={item.disabled && item.disabledReason ? item.disabledReason : item.label}
          placement="top"
          arrow
        >
          <span className={css.slot}>
            <button
              type="button"
              className={classNames(css.button, {
                [css.accent]: item.accent,
                [css.disabled]: item.disabled,
              })}
              // The accessible name follows the tooltip, INCLUDING the reason.
              //
              // It has to: this tooltip hangs off the wrapping span, because a disabled button
              // fires no hover or focus events — which makes the reason mouse-only. A screen
              // reader would otherwise hear "Add media, button, unavailable" and never learn
              // that the library is read-only, with no way to find out.
              //
              // Safe to swap in because every reason LEADS with the verb (see the
              // `<Label> — …` builders in `@lib/verbs`), so the name still starts with what the
              // control does and only then explains. That constraint exists for this.
              //
              // The reasons never say "unavailable" themselves — the `disabled` attribute below
              // already makes assistive tech announce it, and the dimming says it visually. They
              // carry only the WHY.
              aria-label={item.disabled && item.disabledReason ? item.disabledReason : item.label}
              disabled={item.disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (item.disabled) return;
                item.onClick(e);
              }}
            >
              {item.icon}
            </button>
          </span>
        </Tooltip>
      ),
    )}
  </div>
);
